import { hasKey, isObject } from 'utils/obj'

/*
 * Grafana alert rules can fire one instance per series. A Grafana health check
 * narrows the rule to some instances by appending Alertmanager matchers to its
 * alerts URL, so the URL is the only place these options are stored:
 *
 *   .../api/alertmanager/grafana/api/v2/alerts
 *     ?alertId=<uid>
 *     &filter=__alert_rule_uid__="<uid>"   <- added alongside user filters, kept if already there
 *     &filter=service_name="nicole"        <- user filters, ANDed by Grafana
 *     &silenced=false                      <- only when silences should not halt
 */

export const GRAFANA_MATCHER_OPS = ['=', '!=', '=~', '!~'] as const

export type GrafanaMatcherOp = typeof GRAFANA_MATCHER_OPS[number]

export interface GrafanaMatcher {
  label: string
  op: GrafanaMatcherOp
  value: string
}

export interface GrafanaInstance {
  labels: Record<string, string>
  state: string
}

export interface GrafanaAlertOptions {
  filters: GrafanaMatcher[]
  haltWhenSilenced: boolean
}

export interface GrafanaFilterCandidate {
  label: string
  presentOn: number
  values: string[]
  /** Differs between instances (or is missing from some), so filtering on it narrows the rule */
  varies: boolean
}

const FILTER_PARAM = 'filter'
const SILENCED_PARAM = 'silenced'
const RULE_UID_LABEL = '__alert_rule_uid__'
const RULES_API_PATH = 'api/prometheus/grafana/api/v1/rules'

const MATCHER_REGEX =
  /^\s*([^\s!=~"]+)\s*(=~|!~|!=|=)\s*(?:"((?:[^"\\]|\\.)*)"|([^"]*?))\s*$/

// Alertmanager's classic matcher parser, which Grafana falls back to, decodes only
// these escapes and keeps any other (e.g. `\d` in a regex) as written
const MATCHER_UNESCAPES: Record<string, string> = {
  '"': '"',
  '\\': '\\',
  n: '\n'
}

// A backslash before any letter or digit except these means something else in RE2
const RE2_ONLY_ESCAPE_REGEX = /\\(?![bBdDfnrsStvwW])[\dA-Za-z]/

function isMatcherOp(op: string): op is GrafanaMatcherOp {
  return GRAFANA_MATCHER_OPS.some((validOp) => validOp === op)
}

function getLabel(instance: GrafanaInstance, label: string) {
  return hasKey(instance.labels, label) ? instance.labels[label] : undefined
}

function isInternalLabel(label: string) {
  return label.startsWith('__') && label.endsWith('__')
}

function formatGrafanaMatcher({ label, op, value }: GrafanaMatcher) {
  const escapedValue = value
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n')
  return `${label}${op}"${escapedValue}"`
}

function unescapeMatcherValue(value: string) {
  return value.replace(
    /\\(.)/g,
    (escape, char: string) => MATCHER_UNESCAPES[char] ?? escape
  )
}

function parseGrafanaMatcher(matcher: string): GrafanaMatcher | null {
  const match = MATCHER_REGEX.exec(matcher)
  if (!match) return null

  const [, label, op, quotedValue, bareValue] = match
  if (!label || !op || !isMatcherOp(op)) return null

  const value = unescapeMatcherValue(quotedValue ?? bareValue ?? '')

  return { label, op, value }
}

function isRuleUidMatcher(matcher: GrafanaMatcher) {
  return matcher.label === RULE_UID_LABEL && matcher.op === '='
}

function tryParseUrl(url: string) {
  try {
    return new URL(url)
  } catch {
    return null
  }
}

/**
 * Splits a Grafana alerts URL into the URL without our options, and the options.
 * Filters we can't parse stay in the URL so nothing is silently dropped on save,
 * as does a lone rule matcher since we only write ours alongside user filters.
 */
export function splitGrafanaAlertsUrl(url: string): GrafanaAlertOptions & {
  url: string
} {
  const parsedUrl = tryParseUrl(url)
  const defaults = { url, filters: [], haltWhenSilenced: true }

  if (!parsedUrl) return defaults

  const rawFilters = parsedUrl.searchParams.getAll(FILTER_PARAM)
  const silenced = parsedUrl.searchParams.get(SILENCED_PARAM)

  if (!rawFilters.length && silenced === null) return defaults

  const unparsedFilters: string[] = []
  const ruleUidFilters: string[] = []
  const filters: GrafanaMatcher[] = []

  rawFilters.forEach((rawFilter) => {
    const matcher = parseGrafanaMatcher(rawFilter)
    if (!matcher) {
      unparsedFilters.push(rawFilter)
    } else if (isRuleUidMatcher(matcher)) {
      ruleUidFilters.push(rawFilter)
    } else {
      filters.push(matcher)
    }
  })

  const keptFilters = filters.length
    ? unparsedFilters
    : [...unparsedFilters, ...ruleUidFilters]

  parsedUrl.searchParams.delete(FILTER_PARAM)
  parsedUrl.searchParams.delete(SILENCED_PARAM)
  keptFilters.forEach((rawFilter) =>
    parsedUrl.searchParams.append(FILTER_PARAM, rawFilter)
  )

  return {
    url: parsedUrl.toString(),
    filters,
    haltWhenSilenced: silenced !== 'false'
  }
}

export function splitSilencedParam(url: string): {
  silenced: string | null
  url: string
} {
  const parsedUrl = tryParseUrl(url)
  const silenced = parsedUrl?.searchParams.get(SILENCED_PARAM) ?? null

  if (!parsedUrl || silenced === null) return { silenced: null, url }

  parsedUrl.searchParams.delete(SILENCED_PARAM)
  return { silenced, url: parsedUrl.toString() }
}

/**
 * Points any rule matcher at `ruleUid`, so editing the alert ID can't leave a stale
 * one hiding this rule's alerts. Returns the URL untouched when nothing is stale.
 */
export function withCurrentRuleUid(url: string, ruleUid: string): string {
  const parsedUrl = tryParseUrl(url)
  if (!parsedUrl) return url

  const rawFilters = parsedUrl.searchParams.getAll(FILTER_PARAM)
  const isStaleRuleUidFilter = (rawFilter: string) => {
    const matcher = parseGrafanaMatcher(rawFilter)
    return (
      matcher !== null && isRuleUidMatcher(matcher) && matcher.value !== ruleUid
    )
  }

  if (!rawFilters.some(isStaleRuleUidFilter)) return url

  const currentRuleUidFilter = formatGrafanaMatcher({
    label: RULE_UID_LABEL,
    op: '=',
    value: ruleUid
  })

  parsedUrl.searchParams.delete(FILTER_PARAM)
  rawFilters.forEach((rawFilter) =>
    parsedUrl.searchParams.append(
      FILTER_PARAM,
      isStaleRuleUidFilter(rawFilter) ? currentRuleUidFilter : rawFilter
    )
  )

  return parsedUrl.toString()
}

/**
 * Applies alert options to a Grafana alerts URL, replacing any already there.
 * Filters we can't parse aren't ours to replace, so they're kept.
 * The result is percent-encoded. Returns the URL untouched when there is nothing to apply.
 */
export function withGrafanaAlertOptions(
  url: string,
  ruleUid: string,
  { filters, haltWhenSilenced }: GrafanaAlertOptions
): string {
  const parsedUrl = tryParseUrl(url)
  if (!parsedUrl) return url

  const hasExistingOptions =
    parsedUrl.searchParams.has(FILTER_PARAM) ||
    parsedUrl.searchParams.has(SILENCED_PARAM)

  if (!filters.length && haltWhenSilenced && !hasExistingOptions) return url

  const existingMatchers = parsedUrl.searchParams
    .getAll(FILTER_PARAM)
    .map((rawFilter) => ({
      matcher: parseGrafanaMatcher(rawFilter),
      rawFilter
    }))
  const unparsedFilters = existingMatchers
    .filter(({ matcher }) => !matcher)
    .map(({ rawFilter }) => rawFilter)
  const hasRuleUidMatcher = existingMatchers.some(
    ({ matcher }) => matcher && isRuleUidMatcher(matcher)
  )

  parsedUrl.searchParams.delete(FILTER_PARAM)
  parsedUrl.searchParams.delete(SILENCED_PARAM)

  unparsedFilters.forEach((rawFilter) =>
    parsedUrl.searchParams.append(FILTER_PARAM, rawFilter)
  )

  // Rewritten with the current UID so a stale one can't hide this rule's alerts
  if (filters.length || hasRuleUidMatcher) {
    parsedUrl.searchParams.append(
      FILTER_PARAM,
      formatGrafanaMatcher({ label: RULE_UID_LABEL, op: '=', value: ruleUid })
    )
  }
  filters.forEach((filter) =>
    parsedUrl.searchParams.append(FILTER_PARAM, formatGrafanaMatcher(filter))
  )

  if (!haltWhenSilenced) {
    parsedUrl.searchParams.set(SILENCED_PARAM, 'false')
  }

  return parsedUrl.toString()
}

export function buildGrafanaRulesUrl(baseUrl: string, ruleUid: string) {
  const base = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`
  const rulesUrl = new URL(RULES_API_PATH, base)
  rulesUrl.searchParams.set('rule_uid', ruleUid)
  return rulesUrl.toString()
}

function toStringRecord(value: unknown): Record<string, string> {
  if (!isObject(value)) return {}

  return Object.fromEntries(
    Object.entries(value).filter(
      (entry): entry is [string, string] => typeof entry[1] === 'string'
    )
  )
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : []
}

/**
 * Parses a Grafana Prometheus-compatible rules response
 * (`data.groups[].rules[].alerts[]`) into the instances of one rule.
 * Returns null when the instances can't be determined (bad JSON, rule missing,
 * or a Grafana version that doesn't list `alerts`).
 */
export function parseGrafanaRuleInstances(
  responseBody: string,
  ruleUid: string
): GrafanaInstance[] | null {
  let body: unknown
  try {
    body = JSON.parse(responseBody)
  } catch {
    return null
  }

  if (!isObject(body) || !isObject(body.data)) return null

  const rules = asArray(body.data.groups)
    .flatMap((group) => (isObject(group) ? asArray(group.rules) : []))
    .filter(isObject)

  // Older Grafana ignores `rule_uid` and may not return `uid`, so only trust a lone rule
  const [onlyRule] = rules
  const rule =
    rules.find((candidate) => candidate.uid === ruleUid) ??
    (rules.length === 1 && onlyRule?.uid === undefined ? onlyRule : undefined)

  if (!rule || !Array.isArray(rule.alerts)) return null

  return rule.alerts.filter(isObject).map((alert) => ({
    labels: toStringRecord(alert.labels),
    state: typeof alert.state === 'string' ? alert.state : ''
  }))
}

/**
 * Every non-internal label on the rule's instances, with the labels that vary
 * between instances first since those are the ones that narrow the rule.
 */
export function getFilterCandidates(
  instances: GrafanaInstance[]
): GrafanaFilterCandidate[] {
  const labels = new Set(
    instances.flatMap((instance) => Object.keys(instance.labels))
  )

  return [...labels]
    .filter((label) => !isInternalLabel(label))
    .map((label) => {
      const presentValues = instances
        .map((instance) => getLabel(instance, label))
        .filter((value): value is string => value !== undefined)

      const values = [...new Set(presentValues)].sort()

      return {
        label,
        presentOn: presentValues.length,
        values,
        varies: values.length > 1 || presentValues.length < instances.length
      }
    })
    .sort(
      (a, b) =>
        Number(b.varies) - Number(a.varies) || a.label.localeCompare(b.label)
    )
}

/*
 * Grafana matches regexes with RE2. JS covers the common subset, so we use it for
 * previews only. Patterns JS can't compile aren't previewed, and neither are:
 * - groups: JS backtracks, so `(.+)+-prod` can freeze the form for minutes
 * - POSIX classes and RE2-only escapes (`[[:digit:]]`, `\pL`), which JS reads as literals
 */
function compileAnchoredRegex(pattern: string) {
  if (
    pattern.includes('(') ||
    pattern.includes('[:') ||
    RE2_ONLY_ESCAPE_REGEX.test(pattern)
  ) {
    return null
  }

  try {
    return new RegExp(`^(?:${pattern})$`)
  } catch {
    return null
  }
}

function isPreviewable({ op, value }: GrafanaMatcher) {
  return op === '=' || op === '!=' || compileAnchoredRegex(value) !== null
}

// Prometheus matcher semantics: a missing label matches as the empty string
function matchesInstance(instance: GrafanaInstance, matcher: GrafanaMatcher) {
  const actual = getLabel(instance, matcher.label) ?? ''

  switch (matcher.op) {
    case '=':
      return actual === matcher.value
    case '!=':
      return actual !== matcher.value
    case '=~':
      return compileAnchoredRegex(matcher.value)?.test(actual) ?? false
    case '!~':
      return !(compileAnchoredRegex(matcher.value)?.test(actual) ?? true)
  }
}

/**
 * How many instances all matchers match, or null when a regex can't be previewed.
 */
export function countMatchingInstances(
  instances: GrafanaInstance[],
  matchers: GrafanaMatcher[]
): number | null {
  if (!matchers.every(isPreviewable)) return null

  return instances.filter((instance) =>
    matchers.every((matcher) => matchesInstance(instance, matcher))
  ).length
}
